import {
  TitleBox,
  MetricContainer,
  Description,
  UpdateBodyWrapper,
  BackLink,
  UpdateBodyHeader,
  UpdateBodyContent,
} from "./styled"
import { Title20 } from "components/Typo"
import Button from "components/Buttons/Button"
import { Fragment, useEffect, useState } from "react"
import { FlexBox } from "components/Common/FlexBox"
import HeightInput from "components/inputs/HeightInput"
import DarkSwitch from "components/inputs/DarkSwitch"
import { IBody, IBodyDisplayUnits } from "features/profile/profileTypes"
import {
  cmToMm,
  DEFAULT_CM_BODY_HEIGHT,
  feetInchesToMm,
  MAX_CM_BODY_HEIGHT,
  MIN_CM_BODY_HEIGHT,
  mmToCm,
  mmToFeetInches,
} from "utils/heightConverter"
import {
  getBodyWeightValueFromGram,
  getDefaultBodyWeight,
  getMaxBodyWeight,
  getMinBodyWeight,
  kgToGram,
  poundToGram,
} from "utils/weightConverter"
import { bodyDisplayUnitsOptions } from "config"
import PATHS from "router/paths"
import ChevronLeftBold from "components/Icons/ChevronLefBold"
import {
  useScanMessage,
  useValidateBodyHeight,
  useValidateBodyWeight,
} from "features/profile/hooks/body"
import BodyErrors from "components/onboarding/BodyErrors"
import OneDecimalWheelPicker from "components/inputs/OneDecimalWheelPicker"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import ScanMessageBanner from "components/ScanMessageBanner"

interface IUpdateBodyProps {
  body: IBody
  getBodyLoading: boolean
  bodyDisplayUnits: IBodyDisplayUnits
  getBodyDisplayUnitsLoading: boolean
  setBodyDisplayUnits: (bodyDisplayUnits: IBodyDisplayUnits) => void
  goNextStep: (body: IBody) => void
}

const UpdateBody = (props: IUpdateBodyProps) => {
  const {
    body,
    getBodyLoading,
    bodyDisplayUnits,
    getBodyDisplayUnitsLoading,
    setBodyDisplayUnits,
    goNextStep,
  } = props

  const [gramWeight, setGramWeight] = useState(0)
  const weightError = useValidateBodyWeight(bodyDisplayUnits, gramWeight)

  const [mmHeight, setMmHeight] = useState(0)
  const heightError = useValidateBodyHeight(bodyDisplayUnits, mmHeight)

  const scanMessage = useScanMessage({ gramWeight })

  const [shouldDisplayError, setShouldDisplayError] = useState(false)

  const [feet, setFeet] = useState(0)
  const [inches, setInches] = useState(0)

  const onFeetInchesChange = (feet: number, inches: number) => {
    setFeet(feet)
    setInches(inches)

    const nextMmHeight = feetInchesToMm(feet, inches)
    setMmHeight(nextMmHeight)
  }

  useEffect(() => {
    if (body) {
      setGramWeight(body.weight)
      setMmHeight(body.height)

      const { feet, inches } = mmToFeetInches(body.height)

      setFeet(feet)
      setInches(inches)
    }
  }, [body])

  const onWeightChange = (nextWeight: number) => {
    const nextGramWeight =
      bodyDisplayUnits === "IMPERIAL"
        ? poundToGram(nextWeight)
        : kgToGram(nextWeight)
    setGramWeight(nextGramWeight)
  }

  const onCmHeightChange = (nextCmHeight: number) => {
    const nextMmHeight = cmToMm(nextCmHeight)
    setMmHeight(nextMmHeight)
    const { feet, inches } = mmToFeetInches(nextMmHeight)
    setFeet(feet)
    setInches(inches)
  }

  const onContinue = () => {
    if (heightError || weightError) {
      setShouldDisplayError(true)
      return
    }

    goNextStep({
      weight: gramWeight,
      height: mmHeight,
    })
  }

  const isDisableSubmit = shouldDisplayError && (!!weightError || !!heightError)
  return (
    <Fragment>
      <UpdateBodyWrapper>
        <UpdateBodyHeader>
          <BackLink to={PATHS.app.yourGoal.root}>
            <ChevronLeftBold fill="#4BD9DE" />
          </BackLink>
        </UpdateBodyHeader>

        <UpdateBodyContent>
          <TitleBox>
            <Title20>Body metric</Title20>
            <Description>Data fetched from your ZOZOFIT Profile</Description>
          </TitleBox>

          <MetricContainer>
            <DarkSwitch
              disabled={getBodyDisplayUnitsLoading}
              onChange={(value) =>
                setBodyDisplayUnits(value as IBodyDisplayUnits)
              }
              options={bodyDisplayUnitsOptions}
              value={bodyDisplayUnits}
            />
          </MetricContainer>

          <FlexBox $direction="column" $gap={12}>
            {bodyDisplayUnits === "METRIC" ? (
              <OneDecimalWheelPicker
                label="Height"
                value={mmToCm(mmHeight)}
                maxInteger={MAX_CM_BODY_HEIGHT}
                minInteger={MIN_CM_BODY_HEIGHT}
                defaultInteger={DEFAULT_CM_BODY_HEIGHT}
                onChange={onCmHeightChange}
                detailTrailing="cm"
                isError={shouldDisplayError && !!heightError}
                disabled={getBodyLoading}
              />
            ) : (
              <HeightInput
                feet={feet}
                inches={inches}
                onChange={onFeetInchesChange}
                isError={shouldDisplayError && !!heightError}
                disabled={getBodyLoading}
              />
            )}

            <OneDecimalWheelPicker
              label="Weight"
              value={getBodyWeightValueFromGram(bodyDisplayUnits, gramWeight)}
              maxInteger={getMaxBodyWeight(bodyDisplayUnits)}
              minInteger={getMinBodyWeight(bodyDisplayUnits)}
              onChange={onWeightChange}
              detailTrailing={bodyDisplayUnits === "IMPERIAL" ? "lb" : "kg"}
              isError={shouldDisplayError && !!weightError}
              disabled={getBodyLoading}
              defaultInteger={getDefaultBodyWeight(bodyDisplayUnits)}
            />

            {shouldDisplayError && (
              <BodyErrors weightError={weightError} heightError={heightError} />
            )}
          </FlexBox>

          {scanMessage && <ScanMessageBanner message={scanMessage} />}

          <AppStyleCtaBox style={{ paddingLeft: 0, paddingRight: 0 }}>
            <Button
              $borderLess
              $theme="dark"
              onClick={onContinue}
              $type="primary"
              style={{ width: "100%" }}
              disabled={isDisableSubmit}
            >
              Continue
            </Button>
          </AppStyleCtaBox>
        </UpdateBodyContent>
      </UpdateBodyWrapper>
    </Fragment>
  )
}

export default UpdateBody
